import styled from '@emotion/styled';
import { VirtualItem } from '@tanstack/react-virtual';
import React from 'react';
import { InputSize } from '../input/types';
import { DropdownItem } from './DropdownItem';
import { DropdownInputOption, DropdownOption } from './types';

type InternalOption<OptionValue> = DropdownOption<OptionValue> | DropdownInputOption<OptionValue>;
type InternalOptionWithVirtualItem<OptionValue> = InternalOption<OptionValue> & VirtualItem;

export interface DropdownInternalOptionProps<OptionValue> {
  option: InternalOption<OptionValue> | InternalOptionWithVirtualItem<OptionValue>;
  index: number;
  focusIndex?: number | null;
  selectedIndex?: number | null;
  size?: InputSize;
  optionItemHeight?: number;
  keyExtractor?: (item: DropdownOption<OptionValue>, index: number) => string | number;
  onChange?: (value: OptionValue) => void;
  getItemProps: (props: Record<string, unknown>) => Record<string, unknown>;
  setRefs: (node: HTMLElement | null, index: number) => void;
}

export const DropdownInternalOption = <OptionValue extends string | number>({
  option,
  index,
  selectedIndex,
  focusIndex,
  size: sizeProp,
  optionItemHeight,
  onChange: handleChange,
  getItemProps,
  setRefs,
  keyExtractor,
}: DropdownInternalOptionProps<OptionValue>) => {
  const { disabled, value, label } = option;
  return (
    <InternalDropdownItem
      key={keyExtractor?.({ disabled, value, label }, index)}
      tabIndex={focusIndex === index ? 0 : -1}
      aria-disabled={disabled}
      aria-selected={selectedIndex === index && focusIndex === index}
      data-selected={selectedIndex === index}
      disabled={disabled}
      selected={selectedIndex === index}
      focused={focusIndex === index}
      size={sizeProp}
      optionItemHeight={optionItemHeight}
      {...('start' in option && {
        $translateY: option.start,
      })}
      ref={(node) => setRefs(node, index)}
      {...getItemProps({
        onKeyDown(event: KeyboardEvent) {
          if (event.key === 'Enter' && !disabled) {
            event.preventDefault();
            handleChange?.(value);
          }
          if (event.key === '') {
            event.preventDefault();
          }
        },
        onClick() {
          !disabled && handleChange?.(value);
        },
      })}
    >
      {'displayLabel' in option ? option.displayLabel : label}
    </InternalDropdownItem>
  );
};

const InternalDropdownItem = styled(DropdownItem, {
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $translateY?: number }>`
  width: 100%;
  ${({ $translateY }) =>
    $translateY &&
    `
      position: absolute;
      top: 0;
      left: 0;
      transform: translateY(${$translateY}px);
    `};
`;
